import { Flex, Text, Modal, ModalContent, ModalOverlay, useDisclosure, Box } from '@chakra-ui/react'
import { Button, Checkbox, Column, Icon, Row } from '@ubirider/pick-component-library'
import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RedirectModal } from '../RedirectModal'
import { ErrorModal } from '../ErrorModal/ErrorModal'
import { getVivaWalletPaymentInfo } from '../../api/payment'
import { userTimezone } from '../../App'
import { DateTime } from 'luxon'
import { formatCurrency } from '../../helpers/currency'

import CardIcon from '../../assets/credit_card.svg'
import InfoIcon from '../../assets/information_grey.svg'

export const LicensePlateUnpaidTripsModal = ({ trips, licensePlate, isOpen, onClose }) => {
  const { t } = useTranslation()

  const windowSize = useRef([window.innerWidth, window.innerHeight])

  const { isOpen: isOpenRedirect, onOpen: onOpenRedirect, onClose: onCloseRedirect } = useDisclosure()
  const { isOpen: isOpenError, onOpen: onOpenError, onClose: OnCloseError } = useDisclosure()

  const [selectedTrips, setSelectedTrips] = useState([])
  const [orderCode, setOrderCode] = useState(null)
  const [loading, setLoading] = useState(false)

  const handleTripSelection = (tripID) => {
    setSelectedTrips((prev) =>
      prev.includes(tripID)
        ? prev.filter((id) => id !== tripID)
        : [...prev, tripID]
    )
  }

  const totalAmount = useMemo(() => {
    const sumInCents = trips
      .filter((trip) => selectedTrips.includes(trip.productID))
      .reduce((sum, trip) => sum + Math.round(Number(trip.price) * 100), 0)

    return formatCurrency(sumInCents)
  }, [selectedTrips, trips])

  const retryVivaWalletPayment = async () => {
    setLoading(true)
    try {
      const tripSaleIDs = trips
        .filter(trip => selectedTrips.includes(trip.productID))
        .map(trip => trip.saleID)
      const data = { trips: tripSaleIDs, amount: totalAmount * 100 }
      const res = await getVivaWalletPaymentInfo(data, false)
      setOrderCode(res.data.orderCode)
      onOpenRedirect()
    } catch (e) {
      console.error(e)
      onOpenError()
    }
    setLoading(false)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      trapFocus={false}
      closeOnOverlayClick={true}
      autoFocus={false}
      scrollBehavior='inside'
      size={{ sm: 'full', md: 'full', lg: '', xl: '' }}
      isCentered={windowSize.current[0] >= 960}
    >
      <ModalOverlay />
      <ModalContent
        borderRadius='8px'
        maxW={{ lg: '720px' }}
        overflow='hidden'
      >
        <Flex direction='column' h="100%" bg='#F2F4F7' borderRadius='8px'>
          <Box
            bg='white'
            padding={{ sm: '16px 24px', md: '16px 24px', lg: '32px', xl: '32px' }}
            flexShrink={0}
            boxShadow='0 2px 20px 1px rgba(0,0,0,0.1)'
            borderBottom='1px solid #F3F4F6'
          >
            <Flex
              gap='8px'
              align='center'
              justify='space-between'
            >
              <Column css={{ gap: 4 }}>
                <Text fontSize={{ sm: '20px', lg: '24px' }} fontWeight='700'>
                  {t('acqua_vias.pending_payments')}
                </Text>
                <Row css={{ gap: 4 }}>
                  <Text fontSize={{ sm: '14px', lg: '14px' }}>
                    Placa:
                  </Text>
                  <Text fontSize={{ sm: '14px', lg: '14px' }} fontWeight='700' color='#444CE7'>
                    {licensePlate}
                  </Text>
                </Row>
              </Column>
              <Column css={{ gap: 4, justifyContent: 'flex-end' }}>
                <Text fontSize={{ sm: '14px', lg: '14px' }} color='#99A1AF'>
                  Total selecionado
                </Text>
                <Text fontSize={{ sm: '20px', lg: '20px' }} fontWeight='700' textAlign='end'>
                  R$ {totalAmount || '0,00'}
                </Text>
              </Column>
            </Flex>
          </Box>

          <Flex padding={{ sm: '16px 24px', md: '16px 24px', lg: '24px 32px', xl: '24px 32px' }}>
            <Box
              maxH={{ base: 'none', lg: '350px' }}
              bg='white'
              overflowY='auto'
              border='1px solid #EAECF0'
              borderRadius='16px'
              w='100%'
              sx={{
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#EAECF0',
                  borderRadius: '10px',
                  border: '16px solid white',
                },
                scrollbarWidth: 'thin',
                scrollbarColor: '#EAECF0 transparent',
              }}
            >
              <Flex direction='column' padding='8px'>
                {trips.map((trip, index) => (
                  <Flex
                    cursor='pointer'
                    align='center'
                    gap='16px'
                    padding='16px'
                    borderBottom={index === trips.length - 1 ? 'none' : '1px solid #EAECF0'}
                    key={`${index}_lg`}
                    onClick={() => handleTripSelection(trip.productID)}
                  >
                    <Checkbox
                      checked={selectedTrips.includes(trip.productID)}
                      onChange={() => handleTripSelection(trip.productID)}
                      onClick={() => handleTripSelection(trip.productID)}
                    />
                    <Row css={{ flex: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                      <Column>
                        <Flex gap='16px' align='center'>
                          <Text fontSize={{ sm: '14px', lg: '16px' }} fontWeight='700'>
                            {trip.name}
                          </Text>
                        </Flex>
                        <Text
                          fontSize={{ sm: '14px', lg: '16px' }}
                          fontWeight='600'
                          color='neutral.600'
                        >
                          {DateTime.fromMillis(trip.date).setZone(userTimezone).toFormat('dd/MM/yyyy, HH:mm')}
                        </Text>
                      </Column>
                      <Text
                        fontSize={{ sm: '14px', lg: '16px' }}
                        fontWeight='600'
                        color='neutral.600'
                      >
                        R$ {trip.price}
                      </Text>
                    </Row>
                  </Flex>
                ))}
              </Flex>
            </Box>
          </Flex>

          <Box padding={{ sm: '16px 24px', md: '16px 24px', lg: '16px 32px 32px 32px', xl: '16px 32px 32px 32px' }} flexShrink={0} mt="auto">
            <Flex
              w='100%'
              gap='16px'
              justifyContent='space-between'
              display={{ sm: 'none', md: 'none', lg: 'flex', xl: 'flex' }}
            >
              <Row css={{ gap: 8, alignItems: 'center' }}>
                <Icon src={InfoIcon} />
                <Text
                  fontSize={{ sm: '12px', lg: '12px' }}
                  fontWeight='600'
                  color='#6A7282'
                >
                  Ao clicar em pagar, concorda com nossos Termos de Serviço.
                </Text>
              </Row>
              <Button
                onClick={retryVivaWalletPayment}
                loading={loading}
                style='solid'
                type='secondary'
                disabled={selectedTrips.length === 0}
                label={t('acqua_vias.pay_amount', { amount: `R$ ${totalAmount}` })}
                leftIconProps={{ src: CardIcon }}
              />
            </Flex>
            <Flex
              gap='16px'
              alignSelf='stretch'
              direction='column'
              display={{ sm: 'flex', md: 'flex', lg: 'none', xl: 'none' }}
            >
              <Row css={{ gap: 8, alignItems: 'center' }}>
                <Icon src={InfoIcon} />
                <Text
                  fontSize={{ sm: '12px', lg: '12px' }}
                  fontWeight='600'
                  color='#6A7282'
                >
                  Ao clicar em pagar, concorda com nossos Termos de Serviço.
                </Text>
              </Row>
              <Button
                onClick={retryVivaWalletPayment}
                loading={loading}
                style='solid'
                type='secondary'
                disabled={selectedTrips.length === 0}
                label={t('acqua_vias.pay_amount', { amount: `R$ ${totalAmount}` })}
                leftIconProps={{ src: CardIcon }}
              />
            </Flex>
          </Box>
        </Flex>
      </ModalContent>

      <RedirectModal orderCode={orderCode} isOpen={isOpenRedirect} onOpen={onOpenRedirect} onClose={onCloseRedirect} />
      <ErrorModal
        onConfirm={retryVivaWalletPayment}
        loading={loading}
        title={t('acqua_vias.payment_failed_dialog_title')}
        description={t('acqua_vias.payment_failed_dialog_description')}
        isOpen={isOpenError}
        onOpen={onOpenError}
        onClose={OnCloseError}
      />
    </Modal>
  )
}
